import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Property } from '../data/mockData';
import { supabase, DatabaseProperty } from '../lib/supabase';

interface PropertyContextType {
  properties: Property[];
  loading: boolean;
  updateProperty: (id: string, updatedProperty: Property) => Promise<void>;
  addProperty: (newProperty: Property) => Promise<void>;
  deleteProperty: (id: string) => Promise<void>;
  togglePropertyActive: (id: string) => Promise<void>;
}

const PropertyContext = createContext<PropertyContextType | undefined>(undefined);

export function PropertyProvider({ children }: { children: ReactNode }) {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch properties from Supabase on mount
  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching properties:', error);
        // Fallback to mock data if Supabase fails
        import('../data/mockData').then(module => {
          setProperties(module.properties);
        });
      } else {
        // Map database columns back to frontend format
        const mappedData = (data || []).map(dbProperty => ({
          ...dbProperty,
          mainImage: dbProperty.main_image || dbProperty.mainImage,
          galleryImages: dbProperty.gallery_images || dbProperty.galleryImages || [],
          videoUrl: dbProperty.video_url || dbProperty.videoUrl,
          mapsUrl: dbProperty.maps_url || dbProperty.mapsUrl,
          adminNote: dbProperty.admin_note || dbProperty.adminNote,
          categoryId: dbProperty.category_id || dbProperty.categoryId,
          createdAt: dbProperty.created_at || dbProperty.createdAt
        }));
        setProperties(mappedData);
      }
    } catch (error) {
      console.error('Error:', error);
      // Fallback to mock data
      import('../data/mockData').then(module => {
        setProperties(module.properties);
      });
    } finally {
      setLoading(false);
    }
  };

  const updateProperty = async (id: string, updatedProperty: Property) => {
    try {
      const { error } = await supabase
        .from('properties')
        .update({
          title: updatedProperty.title,
          price: updatedProperty.price,
          location: updatedProperty.location,
          area: updatedProperty.area,
          bedrooms: updatedProperty.bedrooms,
          bathrooms: updatedProperty.bathrooms,
          garages: updatedProperty.garages,
          floor: updatedProperty.floor,
          type: updatedProperty.type,
          category: updatedProperty.category,
          category_id: updatedProperty.categoryId,
          description: updatedProperty.description,
          main_image: updatedProperty.mainImage,
          gallery_images: updatedProperty.galleryImages,
          video_url: updatedProperty.videoUrl,
          maps_url: updatedProperty.mapsUrl,
          admin_note: updatedProperty.adminNote,
          featured: updatedProperty.featured,
          active: updatedProperty.active,
          facilities: updatedProperty.facilities,
          nearby: updatedProperty.nearby,
          coordinates: updatedProperty.coordinates
        })
        .eq('id', id);

      if (error) {
        console.error('Error updating property:', error);
        throw error;
      }

      // Update local state
      setProperties(prev => prev.map(p => p.id === id ? updatedProperty : p));
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  };

  const addProperty = async (newProperty: Property) => {
    try {
      const { data, error } = await supabase
        .from('properties')
        .insert({
          id: newProperty.id,
          title: newProperty.title,
          price: newProperty.price,
          location: newProperty.location,
          area: newProperty.area,
          bedrooms: newProperty.bedrooms,
          bathrooms: newProperty.bathrooms,
          garages: newProperty.garages,
          floor: newProperty.floor,
          type: newProperty.type,
          category: newProperty.category,
          category_id: newProperty.categoryId,
          description: newProperty.description,
          main_image: newProperty.mainImage,
          gallery_images: newProperty.galleryImages,
          video_url: newProperty.videoUrl,
          maps_url: newProperty.mapsUrl,
          admin_note: newProperty.adminNote,
          featured: newProperty.featured,
          active: newProperty.active,
          facilities: newProperty.facilities,
          nearby: newProperty.nearby,
          coordinates: newProperty.coordinates,
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        console.error('Error adding property:', error);
        throw error;
      }

      const mappedData = {
        ...data,
        mainImage: data.main_image || data.mainImage,
        galleryImages: data.gallery_images || data.galleryImages || [],
        videoUrl: data.video_url || data.videoUrl,
        mapsUrl: data.maps_url || data.mapsUrl,
        adminNote: data.admin_note || data.adminNote,
        categoryId: data.category_id || data.categoryId,
        createdAt: data.created_at || data.createdAt
      };

      // Update local state
      setProperties(prev => [mappedData, ...prev]);
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  };

  const deleteProperty = async (id: string) => {
    try {
      const { error } = await supabase
        .from('properties')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting property:', error);
        throw error;
      }

      // Update local state
      setProperties(prev => prev.filter(p => p.id !== id));
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  };

  const togglePropertyActive = async (id: string) => {
    try {
      // Find the property to get current active status
      const property = properties.find(p => p.id === id);
      if (!property) {
        throw new Error('Property not found');
      }

      // Toggle active status
      const newActiveStatus = !property.active;

      const { error } = await supabase
        .from('properties')
        .update({ active: newActiveStatus })
        .eq('id', id);

      if (error) {
        console.error('Error toggling property active status:', error);
        throw error;
      }

      // Update local state
      setProperties(prev => prev.map(p => 
        p.id === id ? { ...p, active: newActiveStatus } : p
      ));
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  };

  return (
    <PropertyContext.Provider value={{
      properties,
      loading,
      updateProperty,
      addProperty,
      deleteProperty,
      togglePropertyActive
    }}>
      {children}
    </PropertyContext.Provider>
  );
}

export function useProperties() {
  const context = useContext(PropertyContext);
  if (context === undefined) {
    throw new Error('useProperties must be used within a PropertyProvider');
  }
  return context;
}
